import { Box, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RankControl from '../../../shared/components/RankControl';
import FloorplanCanvas from './FloorplanCanvas3';
import { GREEN, CHARCOAL } from '../../../theme/tokens2';

// ─── CombinationCard ────────────────────────────────────────────────────────
// One card in the comparison grid. Shows the combination's name/subtitle,
// the RankControl (bump up/down), a small non-interactive floorplan
// thumbnail rendering whatever furniture the user has actually chosen so
// far (not an abstract status marker), and a "Try this arrangement" action.
//
// Props:
//   combination     — a RoomCombinationDto-shaped object
//   furniturePieces — the global FurniturePieceDto[] catalog, passed
//                      straight through to FloorplanCanvas to resolve
//                      each placement's stencil image/dimensions by name
//   isSelected      — boolean; this card is the one currently highlighted (not yet "tried")
//   isFirst / isLast — passed straight through to RankControl
//   onSelect        — fn() called when the card itself is clicked (just highlights it)
//   onTry           — fn() called when "Try this arrangement" is clicked (enters the workspace)
//   onMoveUp / onMoveDown — fn() passed to RankControl

export default function CombinationCard({
  combination,
  furniturePieces,
  isSelected,
  isFirst,
  isLast,
  onSelect,
  onTry,
  onMoveUp,
  onMoveDown,
}) {
  return (
    <Box
      onClick={onSelect}
      sx={{
        cursor: 'pointer',
        borderRadius: '4px',
        padding: '16px',
        border: `1.5px solid ${isSelected ? GREEN : 'rgba(0,0,0,0.14)'}`,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
        boxShadow: isSelected ? '0 8px 24px rgba(0,0,0,0.10)' : 'none',
        transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
        '&:hover': { borderColor: GREEN },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.25 }}>
        <Box>
          <Typography sx={{ fontSize: '14.5px', fontWeight: 600, color: isSelected ? GREEN : CHARCOAL, mb: 0.25 }}>
            {combination.name}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.65)' }}>{combination.subtitle}</Typography>
        </Box>
        <RankControl variant="card" onMoveUp={onMoveUp} onMoveDown={onMoveDown} isFirst={isFirst} isLast={isLast} itemLabel={combination.name} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5, pb: 1.25, width: '100%' }}>
        <Box sx={{ width: '100%', maxHeight: 180, overflow: 'hidden' }}>
          <FloorplanCanvas
            combination={combination}
            furniturePieces={furniturePieces}
            interactive={false}
            maxHeight={180}
          />
        </Box>
      </Box>

      <Box
        component="button"
        onClick={(e) => { e.stopPropagation(); onTry(); }}
        sx={{
          fontSize: '12.5px', fontWeight: 500, color: GREEN,
          background: 'rgba(33,131,51,0.06)',
          border: `1px solid rgba(33,131,51,0.30)`,
          borderRadius: '6px', padding: '7px 12px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
          marginTop: 'auto',
          transition: 'background 0.15s ease',
          '&:hover': { background: 'rgba(33,131,51,0.10)' },
        }}
      >
        Try this arrangement
        <ArrowForwardIcon sx={{ fontSize: '0.875rem' }} />
      </Box>
    </Box>
  );
}